import { readableTextColor, safeLabelColor } from '@/features/labels/utils/label-color';
import { useRef } from 'react';

const PRESETS: readonly string[] = [
  '#e5484d',
  '#ef6c00',
  '#f5a524',
  '#eab308',
  '#84cc16',
  '#30a46c',
  '#12a594',
  '#0091ff',
  '#3e63dd',
  '#8e4ec6',
  '#e93d82',
  '#6b7280',
];

interface LabelColorPickerProps {
  value: string;
  onChange: (hex: string) => void;
  label: string;
  customLabel: string;
}

function Swatch({
  color,
  selected,
  onClick,
  children,
}: {
  color: string;
  selected: boolean;
  onClick: () => void;
  children?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        appearance: 'none',
        width: 34,
        height: 34,
        flex: '0 0 auto',
        padding: 0,
        borderRadius: '50%',
        background: color,
        border: selected ? '2px solid var(--f7-text-color)' : '1px solid rgba(0, 0, 0, 0.12)',
        boxShadow: selected ? '0 0 0 2px var(--f7-page-bg-color)' : undefined,
        color: readableTextColor(color),
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
      }}
    >
      {selected && !children && (
        <i className="f7-icons" style={{ fontSize: 16 }}>
          checkmark
        </i>
      )}
      {children}
    </button>
  );
}

export default function LabelColorPicker({
  value,
  onChange,
  label,
  customLabel,
}: LabelColorPickerProps) {
  const nativeRef = useRef<HTMLInputElement>(null);
  const current = safeLabelColor(value);
  const isPreset = PRESETS.includes(current.toLowerCase());

  return (
    <div className="rsv-chip-group">
      <div className="rsv-chip-label">{label}</div>
      <div className="rsv-chip-row" style={{ gap: 12 }}>
        {PRESETS.map((preset) => (
          <Swatch
            key={preset}
            color={preset}
            selected={isPreset && preset === current.toLowerCase()}
            onClick={() => onChange(preset)}
          />
        ))}
        <Swatch
          color={isPreset ? 'transparent' : current}
          selected={!isPreset}
          onClick={() => nativeRef.current?.click()}
        >
          {isPreset && (
            <i className="f7-icons" style={{ fontSize: 16, color: 'var(--f7-text-color)' }}>
              plus
            </i>
          )}
        </Swatch>
        <input
          ref={nativeRef}
          type="color"
          aria-label={customLabel}
          value={current}
          onChange={(e) => onChange(e.target.value.toLowerCase())}
          style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
        />
      </div>
    </div>
  );
}
